import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  InputBase,
  Autocomplete as MuiAutocomplete,
  Paper,
  PaperProps,
  Popper,
  PopperProps,
  styled,
} from '@mui/material'
import { ReferencedEntity } from '../../../../domain/value-object/ReferencedEntity'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useDebounce } from '../../../hooks/useDebounce'
import { useStableFooter } from '../../../hooks/useStableFooter'
import { MenuItem, MenuItemText } from '../../menu'
import { colorPalette } from '../../../style/colorPalette'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import CloseRounded from '@mui/icons-material/CloseRounded'
import { useExpandableOptionGroups } from '../useExpandableOptionGroups'
import { useLatestSearch } from '../useLatestSearch'

type PureOnChange<Entity extends ReferencedEntity> = (
  v: Entity | undefined
) => void
type OnChange<
  Entity extends ReferencedEntity,
  Disabled extends boolean | undefined = false,
> = Disabled extends false
  ? PureOnChange<Entity>
  : PureOnChange<Entity> | undefined
// 候補の渡し方は2通りで、どちらか一方が必須。両方省略すると候補が常に空になるため、
// 判別可能なユニオンにして型で防ぐ。
type OptionsSource<Entity extends ReferencedEntity> =
  | {
      // 呼び出し側が既に全候補を持っている場合。絞り込みはこのコンポーネントの既定フィルタに任せる
      options: Entity[]
      // 候補を取り直す機会としてフォーカスのたびに呼ぶ。取得に失敗して候補が空のままになった
      // ときの再試行など、呼び出し側が必要に応じて使う。
      onFocus?: () => void
      search?: never
    }
  | {
      // 取得のたびにAPIを呼ぶ場合。フォーカス時と入力の都度呼ばれる
      search: (searchText: string) => Promise<Entity[]>
      options?: never
      onFocus?: never
    }

type AutocompleteProps<
  Entity extends ReferencedEntity,
  Disabled extends boolean | undefined = false,
> = OptionsSource<Entity> & {
  value: Entity | undefined
  onChange: OnChange<Entity, Disabled>
  disabled: Disabled

  renderInput?: (
    params: AutocompleteRenderInputParams,
    value: Entity | undefined
  ) => ReactNode
  renderOption?: (v: Entity) => JSX.Element
  groupBy?: (option: Entity) => string
  // MUIのグルーピングは同じグループの候補が連続して並んでいることを前提とするため、
  // groupBy と組で渡す。
  sortOptions?: (options: Entity[]) => Entity[]
  // グループ見出しの描画。指定しなければグループ名をそのまま出す。
  renderGroupLabel?: (group: string) => ReactNode
  // ここで指定したグループだけを開いた状態で表示する。
  defaultExpandedGroups?: string[]
  // 折りたたみ中のグループの項目は DOM に無く、内容に応じた幅だと開閉のたびにポップアップの幅が
  // 変わってしまう。全項目が収まる幅を呼び出し側が算出して渡し、開閉によらず固定する。
  popperWidth?: number
  // 候補リストの末尾に置く要素。絞り込みのトグルなど。
  listFooter?: ReactNode
}

export const Autocomplete = <
  Entity extends ReferencedEntity,
  Disabled extends boolean | undefined = false,
>({
  value: _value,
  onChange,
  search,
  options: controlledOptions,
  onFocus: onFocusControlled,
  disabled,
  renderInput: _renderInput,
  renderOption: _renderOption,
  groupBy,
  sortOptions,
  renderGroupLabel,
  defaultExpandedGroups,
  popperWidth,
  listFooter,
}: AutocompleteProps<Entity, Disabled>) => {
  const value = useMemo(() => _value || null, [_value])
  const [inputValue, setInputValue] = useState<string>(value ? value.name : '')
  const [fetchedOptions, setFetchedOptions] = useState<Entity[]>(
    value ? [value] : []
  )
  const options = useMemo(() => {
    const source = controlledOptions ?? fetchedOptions
    return sortOptions ? sortOptions(source) : source
  }, [controlledOptions, fetchedOptions, sortOptions])
  const [inputChangedBy, setInputChangedBy] =
    useState<AutocompleteInputChangeReason>('reset')
  const onChangeValue = useCallback(
    (_, v: Entity | null, reason: AutocompleteChangeReason) => {
      onChange && onChange(v || undefined)
    },
    [onChange]
  )
  const onChangeInput = useCallback(
    (_, value: string, reason: AutocompleteInputChangeReason) => {
      setInputValue(value)
      setInputChangedBy(reason)
    },
    []
  )
  const runSearch = useLatestSearch(search, setFetchedOptions)
  const onFocus = useCallback(() => {
    if (controlledOptions !== undefined) {
      onFocusControlled && onFocusControlled()
      return
    }
    runSearch('')
  }, [controlledOptions, onFocusControlled, runSearch])

  // 入力欄には選択済みの値がそのまま入っているため、それは絞り込み文字列として扱わない。
  const isSearching = inputValue.trim().length > 0 && inputValue !== value?.name
  const debouncedValue = useDebounce(inputValue, 300)
  useEffect(() => {
    if (controlledOptions !== undefined) return
    if (['input', 'clear'].includes(inputChangedBy)) {
      runSearch(debouncedValue)
    }
  }, [debouncedValue, inputChangedBy, controlledOptions, runSearch])
  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      if (_renderInput) {
        return _renderInput(params, value || undefined)
      }
      return (
        <TextInput
          {...params.InputProps}
          disabled={params.disabled}
          inputProps={params.inputProps}
        />
      )
    },
    [_renderInput, value]
  )
  const renderOption = useCallback(
    (props, value: Entity) => {
      return (
        <OptionItem {...props}>
          {_renderOption ? (
            _renderOption(value)
          ) : (
            <MenuItemText>{value.name}</MenuItemText>
          )}
        </OptionItem>
      )
    },
    [_renderOption]
  )
  const isSelected = useCallback(
    (option: Entity) => option.uuid === value?.uuid,
    [value]
  )
  const { renderGroup } = useExpandableOptionGroups({
    options,
    isSelected,
    groupBy,
    defaultExpandedGroups,
    renderGroupLabel,
    isSearching,
  })
  // PopperComponent / PaperComponent はインライン関数で渡すと再描画のたびに別コンポーネント扱いに
  // なり、リストボックスが再マウントされてスクロール位置が先頭に戻る。依存を絞ってメモ化する。
  const PopperComponent = useMemo(
    () =>
      function AutocompletePopper(props: PopperProps) {
        return (
          <Popper
            {...props}
            placement="bottom-start"
            // MUIはアンカー幅をinline styleで指定する。既定は内容に応じた幅とし、popperWidth の
            // 指定時はその幅に固定する。どちらもinlineで上書きする。
            style={{ ...props.style, width: popperWidth ?? 'auto' }}
            // このポップアップは幅もホバー色も自前で持つため、テーマの既定スタイルの対象から外す
            // （muiTheme.ts の MuiAutocomplete popper を参照）。
            data-styled-popup=""
          />
        )
      },
    [popperWidth]
  )
  const { footerRef: listFooterRef, hasFooter: hasListFooter } =
    useStableFooter(listFooter)
  const PaperComponent = useMemo(
    () =>
      hasListFooter
        ? function AutocompletePaper({ children, ...props }: PaperProps) {
            return (
              <Paper {...props}>
                {children}
                {listFooterRef.current}
              </Paper>
            )
          }
        : undefined,
    [hasListFooter, listFooterRef]
  )
  const isOptionEqualToValue = useCallback(
    (option: ReferencedEntity, value: ReferencedEntity) => {
      return option.uuid === value.uuid
    },
    []
  )
  const getOptionLabel = useCallback((option: ReferencedEntity) => {
    return option.name
  }, [])
  // options が渡されている間は呼び出し側が全候補を持っているため、既定の入力文字列フィルタを
  // そのまま使う。search 経由の場合は絞り込み自体を search に任せているため、二重に絞り込まない
  // よう候補をそのまま返す。
  const filterOptions = useMemo(
    () =>
      controlledOptions !== undefined ? undefined : (opts: Entity[]) => opts,
    [controlledOptions]
  )
  return (
    <MuiAutocomplete
      // Use sx props since making Autocomplete styled component takes a bit effort.
      sx={{ width: '100%' }}
      disabled={disabled}
      // 非活性時は矢印アイコンを消し、編集不可であることを見た目でも示す
      forcePopupIcon={!disabled}
      value={value}
      onChange={onChangeValue}
      isOptionEqualToValue={isOptionEqualToValue}
      options={options}
      renderInput={renderInput}
      inputValue={inputValue}
      onInputChange={onChangeInput}
      onFocus={onFocus}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      groupBy={groupBy}
      renderGroup={renderGroup}
      PopperComponent={PopperComponent}
      PaperComponent={PaperComponent}
      popupIcon={<PopupIcon />}
      clearIcon={<CloseIcon />}
      filterOptions={filterOptions}
    />
  )
}

// テーマの既定スタイルは data-styled-popup で対象外にしているが、MUI自身が
// .MuiAutocomplete-listbox 配下の .MuiAutocomplete-option.Mui-focused (0,3,0) で
// ホバー色を当てるため、&&& で詳細度を上回る。
// 選択状態は MUI が aria-selected でしか渡さず Mui-selected は付かないため、属性で指定する。
const OptionItem = styled(MenuItem)({
  '&&&.Mui-focused': {
    backgroundColor: colorPalette.skyBlue[0],
  },
  '&&&[aria-selected="true"], &&&[aria-selected="true"].Mui-focused': {
    backgroundColor: colorPalette.skyBlue[1],
  },
})

const TextInput = styled(InputBase)({
  width: '100%',
  color: colorPalette.monotone[10],
  '& .MuiInputBase-input.Mui-disabled': {
    color: colorPalette.monotone[3],
    '-webkit-text-fill-color': colorPalette.monotone[3],
  },
})
const PopupIcon = styled(ExpandMoreRounded)({
  color: colorPalette.monotone[4],
})
const CloseIcon = styled(CloseRounded)({
  color: colorPalette.monotone[4],
  fontSize: '14px',
})
